import { useState } from 'react';
import { useLocation } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CopyIcon, PlugsIcon, PlusIcon } from '@phosphor-icons/react';
import { SEVERITIES, type SourceWithToken } from '@meshalert/contract';
import { ApiError, api } from '../lib/api.ts';
import { SEVERITY_LABEL, relativeTime } from '../lib/format.ts';
import { PageHeader } from '../components/Layout.tsx';
import { useSession } from '../lib/session.tsx';
import {
  Button,
  CodeBlock,
  EmptyState,
  ErrorState,
  Field,
  Input,
  ListSkeleton,
  Panel,
  Select,
  Tag,
  Td,
  Tabs,
  Textarea,
  Th,
} from '../components/ui.tsx';
import { Dialog } from '../components/Dialog.tsx';
import { MonitorsSection } from './sources/MonitorsSection.tsx';
import { ApiDocs } from './sources/ApiDocs.tsx';

const TYPE_LABEL: Record<string, string> = {
  generic: 'Generisch',
  alertmanager: 'Alertmanager',
  grafana: 'Grafana',
  monitor: 'Monitor',
  agent: 'Agent',
};

/**
 * Nur diese drei sind hier wählbar. Ein Monitor entsteht im eigenen Abschnitt
 * mit seiner Konfiguration, ein Agent über seinen Installationsbefehl — beides
 * wäre als blosser Eintrag in einer Auswahlliste eine Quelle, die nichts tut.
 */
const INGEST_TYPES = ['generic', 'alertmanager', 'grafana'] as const;

type Tab = 'sources' | 'monitors' | 'api';

export function SourcesPage() {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const location = useLocation();
  // Wer von einer Monitorseite zurückkommt, will wieder bei den Monitoren landen.
  const [tab, setTab] = useState<Tab>(() => (location.state as { tab?: Tab } | null)?.tab ?? 'sources');
  const [creating, setCreating] = useState(false);
  const [issuedToken, setIssuedToken] = useState<SourceWithToken | null>(null);

  const sources = useQuery({ queryKey: ['sources'], queryFn: () => api.sources.list({ limit: 100 }) });
  const ingesting = (sources.data?.items ?? []).filter((s) => s.type !== 'monitor');

  const rotate = useMutation({
    mutationFn: (id: string) => api.sources.rotateToken(id),
    onSuccess: (result) => {
      setIssuedToken(result);
      void queryClient.invalidateQueries({ queryKey: ['sources'] });
    },
  });

  return (
    <>
      <PageHeader
        title="Quellen"
        description="Woher Alarme kommen: fremde Systeme mit eigenem Token, oder Monitore, die MeshAlert selbst betreibt."
        action={
          tab === 'sources' && can('admin', 'operator') ? (
            <Button variant="primary" icon={<PlusIcon size={15} />} onClick={() => setCreating(true)}>
              Quelle anlegen
            </Button>
          ) : null
        }
      />

      <div className="mb-4">
        <Tabs
          value={tab}
          onChange={setTab}
          items={[
            { value: 'sources', label: 'Quellen', count: ingesting.length },
            { value: 'monitors', label: 'Monitore' },
            { value: 'api', label: 'API' },
          ]}
        />
      </div>

      {issuedToken ? (
        <TokenNotice source={issuedToken} onDismiss={() => setIssuedToken(null)} />
      ) : null}

      {tab === 'monitors' ? <MonitorsSection /> : null}
      {tab === 'api' ? <ApiDocs /> : null}

      <Panel bodyClassName="p-0" className={tab === 'sources' ? undefined : 'hidden'}>
        {sources.isLoading ? (
          <ListSkeleton rows={4} />
        ) : sources.isError ? (
          <ErrorState message="Die Quellen konnten nicht geladen werden." onRetry={() => sources.refetch()} />
        ) : ingesting.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[46rem] border-collapse">
              <thead className="border-b border-line bg-sunken">
                <tr>
                  <Th>Quelle</Th>
                  <Th>Typ</Th>
                  <Th>Standard</Th>
                  <Th className="text-right">Alarme</Th>
                  <Th>Zuletzt</Th>
                  <Th>Token</Th>
                  <Th />
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border-subtle)]">
                {ingesting.map((source) => (
                  <tr key={source.id} className="transition-colors hover:bg-hover">
                    <Td>
                      <span className="block text-[13px] font-medium text-ink">{source.name}</span>
                      <span className="mt-0.5 block font-mono text-[11px] text-ink-3">{source.key}</span>
                    </Td>
                    <Td>{TYPE_LABEL[source.type]}</Td>
                    <Td>{SEVERITY_LABEL[source.default_severity]}</Td>
                    <Td className="tnum text-right">{source.alert_count}</Td>
                    <Td>{source.last_alert_at ? relativeTime(source.last_alert_at) : 'noch nie'}</Td>
                    <Td>
                      <Tag>...{source.token_last_four}</Tag>
                    </Td>
                    <Td className="text-right">
                      {can('admin', 'operator') ? (
                        <Button
                          size="sm"
                          disabled={rotate.isPending}
                          onClick={() => rotate.mutate(source.id)}
                        >
                          Token neu
                        </Button>
                      ) : null}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            icon={<PlugsIcon size={26} weight="light" />}
            title="Noch keine Quelle"
            description="Eine Quelle ist das System, das Alarme schickt: ein Monitoring, ein Cronjob, ein Mesh-Knoten. Sie bekommt beim Anlegen einen Token."
            action={
              can('admin', 'operator') ? (
                <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
                  Erste Quelle anlegen
                </Button>
              ) : null
            }
          />
        )}
      </Panel>

      {creating ? (
        <CreateSourceDialog
          onClose={() => setCreating(false)}
          onCreated={(source) => {
            setIssuedToken(source);
            setCreating(false);
            void queryClient.invalidateQueries({ queryKey: ['sources'] });
          }}
        />
      ) : null}
    </>
  );
}

function TokenNotice({ source, onDismiss }: { source: SourceWithToken; onDismiss: () => void }) {
  const [copied, setCopied] = useState(false);

  const example = `curl -X POST ${source.ingest_url} \\
  -H "Authorization: Bearer ${source.token}" \\
  -H "Content-Type: application/json" \\
  -d '{"title":"Knoten antwortet nicht","severity":"critical","dedup_key":"node-7"}'`;

  return (
    <Panel className="mb-4 border-accent/40" title={`Token für ${source.name}`} action={
      <Button size="sm" variant="ghost" onClick={onDismiss}>
        Verstanden
      </Button>
    }>
      <p className="text-[13px] text-ink-2">
        Dieser Token wird nur jetzt angezeigt. Der Server speichert nur seinen Hash, es gibt also
        keinen zweiten Blick darauf.
      </p>

      <div className="mt-3 flex items-center gap-2 rounded-md border border-line bg-sunken px-3 py-2">
        <code className="min-w-0 flex-1 overflow-x-auto font-mono text-[12px] break-all text-ink">
          {source.token}
        </code>
        <Button
          size="sm"
          icon={<CopyIcon size={14} />}
          onClick={async () => {
            await navigator.clipboard.writeText(source.token);
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
          }}
        >
          {copied ? 'Kopiert' : 'Kopieren'}
        </Button>
      </div>

      <p className="mt-4 mb-1.5 text-[12px] font-medium text-ink-2">So meldet die Quelle einen Alarm</p>
      <CodeBlock copy>{example}</CodeBlock>
      <p className="mt-2 text-[12px] text-ink-3">
        Das vollständige Schema steht im Abschnitt „API".
      </p>
    </Panel>
  );
}

function CreateSourceDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (source: SourceWithToken) => void;
}) {
  const [key, setKey] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<(typeof INGEST_TYPES)[number]>('generic');
  const [defaultSeverity, setDefaultSeverity] = useState<'info' | 'warning' | 'error' | 'critical'>(
    'warning',
  );

  const create = useMutation({
    mutationFn: () =>
      api.sources.create({
        key,
        name,
        description: description || null,
        type,
        default_severity: defaultSeverity,
      }),
    onSuccess: onCreated,
  });

  const fieldErrors = create.error instanceof ApiError ? create.error.fieldErrors() : {};
  const generalError =
    create.error instanceof ApiError && Object.keys(fieldErrors).length === 0
      ? create.error.message
      : null;

  return (
    <Dialog title="Quelle anlegen" onClose={onClose}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <Field label="Name" error={fieldErrors.name}>
          {(id) => (
            <Input
              id={id}
              required
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (key === '') return;
              }}
              placeholder="Mesh Gateway Nord"
            />
          )}
        </Field>

        <Field
          label="Schlüssel"
          hint="Technischer Bezeichner, taucht in Logs und Filtern auf."
          error={fieldErrors.key}
        >
          {(id) => (
            <Input
              id={id}
              required
              value={key}
              onChange={(e) => setKey(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
              placeholder="mesh-gateway-nord"
              className="font-mono"
            />
          )}
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Format" hint="Bestimmt, wie der Payload gelesen wird.">
            {(id) => (
              <Select
                id={id}
                value={type}
                onChange={(e) => setType(e.target.value as typeof type)}
              >
                {INGEST_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {TYPE_LABEL[t]}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field label="Schweregrad" hint="Gilt, wenn die Quelle keinen mitschickt.">
            {(id) => (
              <Select
                id={id}
                value={defaultSeverity}
                onChange={(e) => setDefaultSeverity(e.target.value as typeof defaultSeverity)}
              >
                {[...SEVERITIES].reverse().map((s) => (
                  <option key={s} value={s}>
                    {SEVERITY_LABEL[s]}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>

        <Field label="Notiz" hint="Optional. Wofür ist diese Quelle da?">
          {(id) => (
            <Textarea
              id={id}
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          )}
        </Field>

        {generalError ? (
          <p role="alert" className="text-[13px] text-critical">
            {generalError}
          </p>
        ) : null}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" variant="primary" disabled={create.isPending}>
            {create.isPending ? 'Wird angelegt' : 'Anlegen'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
